(() => {
  const root = document.querySelector('.toypal-redesign[data-toypal-page="home"]');
  const form = root && root.querySelector('[data-story-demo-form]');
  if (!root || !form || form.dataset.ready === 'true') return;
  form.dataset.ready = 'true';

  const settings = Object.freeze({
    apiBaseUrl: root.dataset.storyApiBase || '',
    turnstileSiteKey: root.dataset.storyTurnstileKey || '',
    action: root.dataset.storyAction || 'toypal_story_demo',
    locale: root.dataset.storyLocale || 'en-GB',
    privacyNoticeVersion: root.dataset.storyPrivacyVersion || 'web-demo-2026-09-01'
  });
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const result = root.querySelector('[data-story-result]');
  const submit = root.querySelector('[data-story-submit]');
  const again = root.querySelector('[data-story-again]');
  const edit = root.querySelector('[data-story-edit]');
  const audio = root.querySelector('[data-story-audio]');
  const statusLabel = root.querySelector('[data-story-status-label]');
  const statusTitle = root.querySelector('[data-story-status-title]');
  const statusCopy = root.querySelector('[data-story-status-copy]');
  const progress = root.querySelector('[data-story-progress]');
  const dailyLimitMessage = root.querySelector('[data-story-daily-limit]');
  const challenge = root.querySelector('[data-story-challenge]');
  const nameInput = root.querySelector('#story-name');
  let challengeToken = '';
  let widgetId = null;
  let pollController = null;
  let challengeStarted = false;
  const fieldMessages = Object.freeze({
    name: 'Enter a first name or nickname.',
    age: 'Choose an age range.',
    pronoun: 'Choose she or he.',
    world: 'Choose a story world.',
    adult: 'Confirm that you are an authorised adult and have read the privacy notice.'
  });

  const randomToken = () => {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(24);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  };
  const session = randomToken();

  const sha256Hex = async value => {
    if (!window.crypto || !window.crypto.subtle) throw new Error('secure-context-required');
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const loadTurnstile = () => new Promise((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const existing = document.querySelector('script[data-toypal-turnstile]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.turnstile), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.toypalTurnstile = '';
    script.addEventListener('load', () => resolve(window.turnstile), { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.append(script);
  });

  const initialiseChallenge = async () => {
    if (challengeStarted) return;
    challengeStarted = true;
    if (!challenge || !settings.turnstileSiteKey) return;
    challenge.innerHTML = '<p>Preparing the secure check…</p>';
    try {
      const [turnstile, cData] = await Promise.all([loadTurnstile(), sha256Hex(session)]);
      challenge.textContent = '';
      widgetId = turnstile.render(challenge, {
        sitekey: settings.turnstileSiteKey,
        action: settings.action,
        cData,
        callback: token => { challengeToken = token; },
        'expired-callback': () => { challengeToken = ''; },
        'error-callback': () => { challengeToken = ''; }
      });
    } catch {
      challenge.innerHTML = '<p role="alert">The secure check could not load. Please refresh and try again.</p>';
    }
  };

  const returnToResult = () => {
    window.requestAnimationFrame(() => {
      result.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      result.focus && result.focus({ preventScroll: true });
    });
  };

  const showResult = ({ mode = 'working', label, title, copy, percentage = 12, allowAnother = true, dailyLimit = false }) => {
    form.hidden = true;
    result.hidden = false;
    result.classList.toggle('is-error', mode === 'error');
    result.classList.toggle('is-ready', mode === 'ready');
    result.classList.toggle('is-daily-limit', dailyLimit);
    statusLabel.textContent = label;
    statusTitle.textContent = title;
    statusCopy.textContent = copy;
    progress.style.width = `${percentage}%`;
    again.hidden = (mode !== 'ready' && mode !== 'error') || !allowAnother;
    edit.hidden = dailyLimit;
    progress.hidden = dailyLimit;
    audio.hidden = dailyLimit || !audio.src;
    if (dailyLimitMessage) dailyLimitMessage.hidden = !dailyLimit;
    result.focus && result.focus({ preventScroll: true });
    if (mode === 'ready' || mode === 'error') returnToResult();
  };

  const statusContent = {
    queued: ['Story received', 'Your story is waiting its turn…', 'We will begin creating it in a moment.', 18],
    planning: ['Creating your story', 'The story is taking shape…', 'ToyPal is putting the safe, personalised scene together.', 45],
    narrating: ['Creating the voice', 'Now it is finding its voice…', 'The complete scene is being narrated with a consistent voice and natural pacing.', 76]
  };

  const errorCopy = code => {
    if (code === 'demo-budget-paused') return ['Story samples are paused', 'Today’s story samples have all been made. Please try again later.'];
    if (code === 'demo-expired') return ['This sample has expired', 'Create a new sample whenever you are ready.'];
    if (code === 'demo-daily-limit' || code === 'demo-network-limit') return ['Today’s sample is complete', 'This browser or household has already created its temporary story sample today.'];
    if (code === 'story-demo-disabled') return ['Story samples are paused', 'The story creator is temporarily unavailable. Please try again later.'];
    if (code === 'demo-origin-not-allowed') return ['Website connection needs attention', 'The secure story service does not yet recognise this website address.'];
    return ['We’re sorry—your story could not be created just now', 'No child details were saved. Please try again in a little while.'];
  };

  const showError = code => {
    const [title, copy] = errorCopy(code);
    const dailyLimitReached = code === 'demo-daily-limit' || code === 'demo-network-limit';
    showResult({
      mode: 'error',
      label: dailyLimitReached ? 'Daily story limit reached' : 'Story unavailable',
      title,
      copy,
      percentage: 100,
      allowAnother: !dailyLimitReached,
      dailyLimit: dailyLimitReached
    });
  };

  const readErrorCode = problem => problem?.error?.code || problem?.code || problem?.errorCode || 'demo-generation-unavailable';

  const fieldIsValid = field => {
    const kind = field.dataset.storyField;
    if (kind === 'name') return /^[\p{L} '’\-]+$/u.test(nameInput.value.trim().replace(/\s+/g, ' '));
    const control = field.querySelector('input');
    if (!control) return true;
    if (control.type === 'radio') return Boolean(field.querySelector('input:checked'));
    if (control.type === 'checkbox') return control.checked;
    return control.value.trim() !== '';
  };

  const updateFieldState = field => {
    const valid = fieldIsValid(field);
    const error = field.querySelector('[data-story-error]');
    field.classList.toggle('has-error', !valid);
    field.querySelectorAll('input').forEach(control => {
      if (valid) control.removeAttribute('aria-invalid');
      else control.setAttribute('aria-invalid', 'true');
    });
    if (error) {
      error.textContent = valid ? '' : fieldMessages[field.dataset.storyField];
      error.hidden = valid;
    }
    return valid;
  };

  const validateFields = () => {
    const fields = Array.from(form.querySelectorAll('[data-story-field]'));
    let firstInvalid = null;
    fields.forEach(field => {
      if (!updateFieldState(field) && !firstInvalid) firstInvalid = field;
    });
    if (!firstInvalid) return true;
    firstInvalid.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
    firstInvalid.querySelector('input')?.focus({ preventScroll: true });
    return false;
  };

  const poll = async ({ demoId, accessToken, expiresAt }) => {
    pollController?.abort();
    pollController = new AbortController();
    const baseUrl = settings.apiBaseUrl.replace(/\/$/, '');
    const expiry = Date.parse(expiresAt);
    while (!pollController.signal.aborted && (!Number.isFinite(expiry) || Date.now() < expiry)) {
      await new Promise(resolve => window.setTimeout(resolve, 1800));
      let response;
      try {
        response = await fetch(`${baseUrl}/v1/public/story-demos/${encodeURIComponent(demoId)}`, {
          method: 'GET',
          headers: { 'X-ToyPal-Demo-Access': accessToken },
          cache: 'no-store',
          credentials: 'omit',
          referrerPolicy: 'no-referrer',
          signal: pollController.signal
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        continue;
      }
      if (!response.ok) {
        if (response.status >= 500) continue;
        showError('demo-generation-unavailable');
        return;
      }
      const state = await response.json();
      if (state.status === 'ready' && state.playback?.url) {
        audio.src = state.playback.url;
        audio.hidden = false;
        showResult({
          mode: 'ready',
          label: 'Your story is ready',
          title: 'Press play and step into the story.',
          copy: `This ${state.playback.durationSeconds}-second sample is temporary and will not be added to the ToyPal app.`,
          percentage: 100
        });
        return;
      }
      if (state.status === 'failed' || state.status === 'expired') {
        showError(state.errorCode);
        return;
      }
      const content = statusContent[state.status] || statusContent.queued;
      showResult({ label: content[0], title: content[1], copy: content[2], percentage: content[3] });
    }
    if (!pollController.signal.aborted) showError('demo-expired');
  };

  form.querySelectorAll('[data-story-field]').forEach(field => {
    field.addEventListener('input', () => updateFieldState(field));
    field.addEventListener('change', () => updateFieldState(field));
  });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    const formData = new FormData(form);
    const displayName = String(formData.get('displayName') || '').trim().replace(/\s+/g, ' ');
    if (!validateFields()) return;
    if (!settings.apiBaseUrl || !settings.turnstileSiteKey) {
      showError('story-demo-disabled');
      return;
    }
    if (!challengeToken) {
      challenge.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
      return;
    }
    submit.disabled = true;
    showResult({ label: 'Sending your choices', title: 'Your story is beginning…', copy: 'Please keep this page open while we prepare the sample.', percentage: 10 });
    const baseUrl = settings.apiBaseUrl.replace(/\/$/, '');
    try {
      const response = await fetch(`${baseUrl}/v1/public/story-demos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': randomToken(),
          'X-ToyPal-Demo-Challenge': challengeToken,
          'X-ToyPal-Demo-Session': session
        },
        body: JSON.stringify({
          locale: settings.locale,
          displayName,
          ageBand: formData.get('ageBand'),
          pronoun: formData.get('pronoun'),
          storyWorld: formData.get('storyWorld'),
          adultConfirmed: true,
          privacyNoticeVersion: settings.privacyNoticeVersion
        }),
        cache: 'no-store',
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      });
      challengeToken = '';
      if (!response.ok) {
        let problem = {};
        try { problem = await response.json(); } catch {}
        showError(readErrorCode(problem));
        return;
      }
      const accepted = await response.json();
      const content = statusContent.queued;
      showResult({ label: content[0], title: content[1], copy: content[2], percentage: content[3] });
      await poll({ demoId: accepted.demoId, accessToken: accepted.demoAccessToken, expiresAt: accepted.expiresAt });
    } catch {
      showError('demo-generation-unavailable');
    } finally {
      submit.disabled = false;
      if (window.turnstile && widgetId !== null) window.turnstile.reset(widgetId);
    }
  });

  const reset = () => {
    pollController?.abort();
    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    audio.hidden = true;
    result.hidden = true;
    form.hidden = false;
    again.hidden = true;
    edit.hidden = false;
    progress.hidden = false;
    if (dailyLimitMessage) dailyLimitMessage.hidden = true;
    result.classList.remove('is-daily-limit');
    form.querySelectorAll('[data-story-field]').forEach(field => {
      field.classList.remove('has-error');
      field.querySelectorAll('[aria-invalid]').forEach(control => control.removeAttribute('aria-invalid'));
      const error = field.querySelector('[data-story-error]');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    });
  };
  edit.addEventListener('click', reset);
  again.addEventListener('click', () => {
    form.reset();
    reset();
  });
  window.addEventListener('pagehide', () => pollController?.abort());
  form.addEventListener('focusin', initialiseChallenge, { once: true });
  form.addEventListener('pointerdown', initialiseChallenge, { once: true });
  if ('IntersectionObserver' in window) {
    const challengeObserver = new IntersectionObserver(entries => {
      if (!entries.some(entry => entry.isIntersecting)) return;
      challengeObserver.disconnect();
      initialiseChallenge();
    }, { rootMargin: '240px 0px' });
    challengeObserver.observe(form);
  }
})();
